import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCreation,
  getVocabulary,
  patchProvenance,
  patchAnalysis,
  generateDialogue,
  acceptDateReminder,
  declineDateReminder,
  dciError,
  CREATION_STATUS,
} from "../../Services/dialogueCreations";
import { CaptureHeader, CapturePage, CaptureSheet, useFocusedCaptureHeader } from "./shared";
import {
  ANALYZING_COPY,
  SOURCE_COPY,
  SUMMARY_COPY,
  CREATED_COPY,
  READY_COPY,
  FAILURE_COPY,
  errorText,
} from "./copy";
import arisOrb from "../../Assets/icon/capture/aris-orb.png";
import stepDone from "../../Assets/icon/capture/step-done.svg";
import stepActive from "../../Assets/icon/capture/step-active.svg";
import userIcon from "../../Assets/icon/capture/user.svg";
import editIcon from "../../Assets/icon/capture/edit.svg";
import chevronIcon from "../../Assets/icon/capture/chevron-right.svg";
import bulletIcon from "../../Assets/icon/capture/bullet.svg";
import checkSmall from "../../Assets/icon/capture/check-small.svg";
import doneCheck from "../../Assets/icon/capture/done-check.svg";
import ring1 from "../../Assets/icon/capture/ring1.svg";
import ring2 from "../../Assets/icon/capture/ring2.svg";
import ring3 from "../../Assets/icon/capture/ring3.svg";
import ring4 from "../../Assets/icon/capture/ring4.svg";
import ring5 from "../../Assets/icon/capture/ring5.svg";
import confetti from "../../Assets/icon/capture/confetti.svg";

/**
 * The Capture flow after input (Figma 9856:14762 → 9856:15274; guide §1–§3).
 *
 * The server owns the state machine: this screen polls `GET /{id}` and
 * renders whatever `status` says, moving state only through the documented
 * calls — Confirm Source (`PATCH provenance`), Summary (`PATCH analysis` then
 * `POST generate-dialogue`). It never infers a step from timing.
 */

/** The top bar title on every processing step; the step names itself below. */
const FLOW_TITLE = "Capture";

const POLL_MS = 2000;
const SLOW_AFTER_MS = 60000;

const WAITING = new Set([
  CREATION_STATUS.NEEDS_SOURCE_CONFIRMATION,
  CREATION_STATUS.SUMMARY_READY,
  CREATION_STATUS.READY,
  CREATION_STATUS.FAILED_RECOVERABLE,
  CREATION_STATUS.FAILED_TERMINAL,
]);

/* --------------------------------------------------------------- Analyzing */

function Analyzing({ creation, slow, onClose }) {
  const step = creation?.progress_step || 1;
  const pages = creation?.analysis_progress;
  return (
    <>
      <CaptureHeader title={FLOW_TITLE} onBack={onClose} />
      <div className="cap-body cap-analyzing">
        <h2 className="cap-screen-title cap-screen-title--center">{ANALYZING_COPY.title}</h2>
        <img className="cap-orb" src={arisOrb} alt="" width="119" height="119" />
        <p className="cap-analyzing__lead">{ANALYZING_COPY.lead}</p>
        <ol className="cap-card cap-steps">
          {ANALYZING_COPY.steps.map((label, i) => {
            const n = i + 1;
            // Steps can advance faster than the poll: anything below the
            // current step is done, even if it was never seen active.
            const state = n < step ? "done" : n === step ? "active" : "todo";
            return (
              <li key={label} className={`cap-step cap-step--${state}`}>
                {state === "done" && (
                  <span className="cap-check-disc">
                    <img src={stepDone} alt="" width="22" height="22" />
                  </span>
                )}
                {state === "active" && <img src={stepActive} alt="" width="22" height="22" />}
                {state === "todo" && <span className="cap-step__ring" />}
                <span className="cap-step__text">
                  {label}
                  {n === 1 && state === "active" && pages?.pages_total > 0 && (
                    <span className="cap-step__progress">
                      {ANALYZING_COPY.pages(pages.pages_done || 0, pages.pages_total)}
                      <span className="cap-step__bar">
                        <span
                          style={{
                            width: `${Math.min(100, ((pages.pages_done || 0) / pages.pages_total) * 100)}%`,
                          }}
                        />
                      </span>
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
        <p className="cap-analyzing__hint">{slow ? ANALYZING_COPY.slow : ANALYZING_COPY.hint}</p>
      </div>
    </>
  );
}

/* ----------------------------------------------------------- Confirm Source */

const labelFor = (list, value) => (list || []).find((o) => o.value === value)?.label || null;

function ListSheet({ title, options, value, allowCustom, onPick, onClose }) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = (options || []).filter((o) => !q || String(o.label).toLowerCase().includes(q));
  return (
    <CaptureSheet open title={title} onClose={onClose}>
      <label className="cap-search">
        <input
          autoFocus
          value={query}
          placeholder={SOURCE_COPY.search}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      <ul className="cap-options">
        {shown.map((o) => (
          <li key={o.value}>
            <button
              type="button"
              className={`cap-option${o.value === value ? " cap-option--on" : ""}`}
              onClick={() => onPick(o)}
            >
              {o.label}
            </button>
          </li>
        ))}
        {allowCustom && q && !shown.some((o) => String(o.label).toLowerCase() === q) && (
          <li>
            <button
              type="button"
              className="cap-option"
              onClick={() => onPick({ value: query.trim(), label: query.trim(), custom: true })}
            >
              “{query.trim()}”
            </button>
          </li>
        )}
      </ul>
    </CaptureSheet>
  );
}

function TextSheet({ title, initial, onSave, onClose }) {
  const [value, setValue] = useState(initial || "");
  return (
    <CaptureSheet open title={title} onClose={onClose}>
      <input
        className="cap-input"
        autoFocus
        value={value}
        maxLength={200}
        onChange={(e) => setValue(e.target.value)}
      />
      <div className="cap-sheet__buttons">
        <button type="button" className="cap-btn cap-btn--outline" onClick={onClose}>
          {SOURCE_COPY.cancel}
        </button>
        <button type="button" className="cap-btn" onClick={() => onSave(value.trim())}>
          {SOURCE_COPY.update}
        </button>
      </div>
    </CaptureSheet>
  );
}

/**
 * Confirm Source (Figma 9856:14804 + edit sheets). Each edit is sent as its
 * own PATCH with only that field — member edits become authoritative. The
 * model's confidence is never on the wire, so the design's "95% confidence"
 * line has nothing to show and is left out; an uncertain guess simply arrives
 * as blank fields. Dropdown lists come from the vocabulary, never hardcoded;
 * an entry with `requires_free_text` opens a text field.
 */
function ConfirmSource({ creation, vocabulary, onUpdate, onClose }) {
  const id = creation.creation_request_id;
  const p = creation.provenance || {};
  const [sheet, setSheet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const send = async (fields) => {
    setBusy(true);
    setError(null);
    try {
      const next = await patchProvenance(id, fields);
      onUpdate(next);
      setSheet(null);
    } catch (e) {
      setError(errorText(dciError(e).code));
    } finally {
      setBusy(false);
    }
  };

  // DCI v27 (aris-ai plan 53): *Source* is the 10-value origin category from the
  // vocabulary (v2). Older servers without `source_categories` keep the role list.
  const categories = vocabulary?.source_categories;
  const useCategories = Array.isArray(categories) && categories.length > 0;
  const category = useCategories
    ? categories.find((c) => c.value === p.source_category)
    : null;
  const roleLabel = useCategories
    ? category?.label
    : p.source_role === "OTHER" && p.source_role_other
      ? p.source_role_other
      : labelFor(vocabulary?.source_roles, p.source_role);
  const relationshipLabel = labelFor(vocabulary?.relationships, p.relationship) || p.relationship;
  // Relationship is meaningless for a book, a website, your own notes or Unknown.
  const showRelationship = !useCategories || !category || category.shows_relationship;

  const rows = [
    { key: "provider_name", label: SOURCE_COPY.providedBy, value: p.provider_name },
    showRelationship && {
      key: "relationship",
      label: SOURCE_COPY.relationship,
      value: relationshipLabel,
    },
    { key: "title", label: SOURCE_COPY.titleLabel, value: p.title },
  ].filter(Boolean);
  const sourceSheet = useCategories ? "source_category" : "source_role";

  return (
    <>
      <CaptureHeader title={FLOW_TITLE} onBack={onClose} />
      <div className="cap-body">
        <div className="cap-screen-head">
          <h2 className="cap-screen-title">{SOURCE_COPY.title}</h2>
          <p className="cap-screen-lead">{SOURCE_COPY.lead}</p>
        </div>

        <button type="button" className="cap-card cap-source" onClick={() => setSheet(sourceSheet)}>
          <span className="cap-field__label">{SOURCE_COPY.sourceType}</span>
          <span className="cap-source__row">
            <span className="cap-tile cap-tile--blue">
              <img src={userIcon} alt="" width="24" height="24" />
            </span>
            <span className="cap-source__value">{roleLabel || SOURCE_COPY.notSet}</span>
            <img src={chevronIcon} alt="" width="16" height="16" />
          </span>
        </button>

        <div className="cap-card cap-fields">
          {rows.map((r) => (
            <button key={r.key} type="button" className="cap-field" onClick={() => setSheet(r.key)}>
              <span className="cap-field__text">
                <span className="cap-field__label">{r.label}</span>
                <span className={`cap-field__value${r.value ? "" : " cap-field__value--empty"}`}>
                  {r.value || SOURCE_COPY.notSet}
                </span>
              </span>
              <img src={editIcon} alt="" width="16" height="16" />
            </button>
          ))}
        </div>

        {error && <p className="cap-error">{error}</p>}
        <button type="button" className="cap-btn" disabled={busy} onClick={() => send({ confirm: true })}>
          {SOURCE_COPY.confirm}
        </button>
      </div>

      {sheet === "source_category" && (
        <ListSheet
          title={SOURCE_COPY.sheets.source_category}
          options={categories}
          value={p.source_category}
          onClose={() => setSheet(null)}
          onPick={(o) => send({ source_category: o.value })}
        />
      )}
      {sheet === "source_role" && (
        <ListSheet
          title={SOURCE_COPY.sheets.source_role}
          options={vocabulary?.source_roles}
          value={p.source_role}
          onClose={() => setSheet(null)}
          onPick={(o) =>
            o.requires_free_text ? setSheet("source_role_other") : send({ source_role: o.value })
          }
        />
      )}
      {sheet === "source_role_other" && (
        <TextSheet
          title={SOURCE_COPY.sheets.other}
          initial={p.source_role_other}
          onClose={() => setSheet(null)}
          onSave={(v) => v && send({ source_role: "OTHER", source_role_other: v })}
        />
      )}
      {sheet === "relationship" && (
        <ListSheet
          title={SOURCE_COPY.sheets.relationship}
          options={vocabulary?.relationships}
          value={p.relationship}
          allowCustom
          onClose={() => setSheet(null)}
          onPick={(o) => send({ relationship: o.value })}
        />
      )}
      {(sheet === "provider_name" || sheet === "title") && (
        <TextSheet
          title={SOURCE_COPY.sheets[sheet]}
          initial={p[sheet]}
          onClose={() => setSheet(null)}
          onSave={(v) => send({ [sheet]: v })}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------- Summary & Insights */

/**
 * Summary & Insights (Figma 9856:15177). Items flagged `needs_verification`
 * (an uncertain dose, deadline or amount) are marked "Please verify", never
 * shown as fact. Looks Good confirms, then starts generation (202, polled).
 */
function Summary({ creation, onUpdate, onClose }) {
  const id = creation.creation_request_id;
  const a = creation.analysis || {};
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const confirmed = await patchAnalysis(id, { confirm: true });
      onUpdate(confirmed);
      const started = await generateDialogue(id);
      if (started?.creation) onUpdate(started.creation);
    } catch (e) {
      setError(errorText(dciError(e).code));
      setBusy(false);
    }
  };

  return (
    <>
      <CaptureHeader title={FLOW_TITLE} onBack={onClose} />
      <div className="cap-body">
        <div className="cap-screen-head">
          <h2 className="cap-screen-title">{SUMMARY_COPY.title}</h2>
          <p className="cap-screen-lead">{SUMMARY_COPY.lead}</p>
        </div>

        {a.summary && (
          <section className="cap-card cap-block">
            <h3 className="cap-field__label">{SUMMARY_COPY.summary}</h3>
            <p className="cap-block__text">{a.summary}</p>
          </section>
        )}

        {Array.isArray(a.takeaways) && a.takeaways.length > 0 && (
          <section className="cap-card cap-block cap-block--strong">
            <h3 className="cap-field__label">{SUMMARY_COPY.takeaways}</h3>
            <ul className="cap-list">
              {a.takeaways.map((t) => (
                <li key={t} className="cap-list__item">
                  <img src={bulletIcon} alt="" width="6" height="6" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {Array.isArray(a.actions) && a.actions.length > 0 && (
          <section className="cap-card cap-block">
            <h3 className="cap-field__label">{SUMMARY_COPY.actions}</h3>
            <ul className="cap-list">
              {a.actions.map((act) => (
                <li key={act.text} className="cap-list__item">
                  <img src={checkSmall} alt="" width="14" height="14" />
                  <span>
                    {act.text}
                    {act.needs_verification && (
                      <span className="cap-verify">{SUMMARY_COPY.verify}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {error && <p className="cap-error">{error}</p>}
        <button type="button" className="cap-btn" disabled={busy} onClick={confirm}>
          {SUMMARY_COPY.confirm}
        </button>
      </div>
    </>
  );
}

/* ----------------------------------------------------------------- Created */

/**
 * "I've created …" (Figma 9856:15249), without the Guided Journey chip and
 * "Why a Guided Journey?" sheet: the classes are retired as member-facing
 * labels (v26.5), so `classification` is not shown.
 */
function Created({ onContinue, onClose }) {
  return (
    <>
      <CaptureHeader title={FLOW_TITLE} onBack={onClose} />
      <div className="cap-body cap-created">
        <img className="cap-orb cap-orb--small" src={arisOrb} alt="" width="72" height="72" />
        <h2 className="cap-created__title">{CREATED_COPY.title}</h2>
        <p className="cap-created__body">{CREATED_COPY.body}</p>
        <button type="button" className="cap-btn" onClick={onContinue}>
          {CREATED_COPY.continue}
        </button>
      </div>
    </>
  );
}

/* -------------------------------------------------------------- All set */

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/**
 * One source date (guide §7.3a). Dates are suggestions: nothing is scheduled
 * until the member taps Remind me. A date that needs verification, has no
 * parsed date, or has passed is shown as information only. `reminder_scheduled:
 * null` means the server could not read the state this time — no state shown.
 */
function DateRow({ creationId, index, entry, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const when = formatDate(entry.date);
  const past = entry.date && new Date(entry.date).getTime() < Date.now();
  const schedulable = Boolean(entry.date) && !entry.needs_verification && !past;

  const act = async (accept) => {
    setBusy(true);
    setError(null);
    try {
      const res = accept
        ? await acceptDateReminder(creationId, index)
        : await declineDateReminder(creationId, index);
      onChange(index, {
        reminder_scheduled: Boolean(res?.reminder_scheduled),
        reminder_id: res?.reminder_id ?? null,
        delivery_scheduled: res?.delivery_scheduled,
      });
    } catch (e) {
      const err = dciError(e);
      setError(err.message || errorText(err.code));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="cap-date">
      <span className="cap-date__text">
        <span className="cap-date__label">{entry.label}</span>
        <span className="cap-date__when">{when || entry.date_text}</span>
        {error && <span className="cap-error">{error}</span>}
      </span>
      {!schedulable ? (
        entry.needs_verification ? <span className="cap-verify">{READY_COPY.verifyDate}</span> : null
      ) : entry.reminder_scheduled === null ? null : entry.reminder_scheduled ? (
        <span className="cap-date__done">
          {entry.delivery_scheduled === false ? READY_COPY.savedNoPush : READY_COPY.reminded}
          <button type="button" className="cap-link" disabled={busy} onClick={() => act(false)}>
            {READY_COPY.undo}
          </button>
        </span>
      ) : (
        <button type="button" className="cap-chip-btn" disabled={busy} onClick={() => act(true)}>
          {READY_COPY.remind}
        </button>
      )}
    </li>
  );
}

function Ready({ creation, onClose }) {
  const navigate = useNavigate();
  const [dates, setDates] = useState(() => creation.analysis?.dates || []);
  useEffect(() => setDates(creation.analysis?.dates || []), [creation.analysis]);

  const templateId = creation.dialogue_template_id;
  return (
    <div className="cap-ready">
      <CaptureHeader title={FLOW_TITLE} onBack={onClose} />
      <div className="cap-body">
        <div className="cap-ready__visual" aria-hidden="true">
          <img className="cap-ready__confetti" src={confetti} alt="" width="330" height="141" />
          {[ring5, ring4, ring3, ring2, ring1].map((r) => (
            <img key={r} className="cap-ready__ring" src={r} alt="" />
          ))}
          <img className="cap-ready__orb" src={arisOrb} alt="" width="69" height="69" />
        </div>
        <div className="cap-ready__text">
          <h2 className="cap-ready__title">{READY_COPY.title}</h2>
          <p className="cap-ready__body">{READY_COPY.body}</p>
        </div>
        <ul className="cap-ready__checks">
          {READY_COPY.checks.map((c) => (
            <li key={c}>
              <span className="cap-check-disc">
                <img src={doneCheck} alt="" width="22" height="22" />
              </span>
              {c}
            </li>
          ))}
        </ul>

        {dates.length > 0 && (
          <section className="cap-card cap-block">
            <h3 className="cap-field__label">{READY_COPY.datesTitle}</h3>
            <p className="cap-screen-lead">{READY_COPY.datesLead}</p>
            <ul className="cap-dates">
              {dates.map((entry, i) => (
                <DateRow
                  key={`${entry.label}-${i}`}
                  creationId={creation.creation_request_id}
                  index={i}
                  entry={entry}
                  onChange={(idx, patch) =>
                    setDates((prev) => prev.map((d, j) => (j === idx ? { ...d, ...patch } : d)))
                  }
                />
              ))}
            </ul>
          </section>
        )}

        {templateId && (
          <button
            type="button"
            className="cap-btn"
            onClick={() => navigate(`/explore/dialogue/${encodeURIComponent(templateId)}`)}
          >
            {READY_COPY.cta}
          </button>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- Failure */

function Failure({ creation, onRetry, onClose }) {
  const navigate = useNavigate();
  const terminal = creation.status === CREATION_STATUS.FAILED_TERMINAL;
  const code = creation.error_code;
  const message = terminal ? FAILURE_COPY.terminal : FAILURE_COPY[code] || FAILURE_COPY.terminal;
  const canRetry = !terminal && code === "DIALOGUE_GENERATION_FAILED";
  return (
    <>
      <CaptureHeader title={FLOW_TITLE} onBack={onClose} />
      <div className="cap-body cap-created">
        <h2 className="cap-created__title">{FAILURE_COPY.title}</h2>
        <p className="cap-created__body">{message}</p>
        {canRetry ? (
          <button type="button" className="cap-btn" onClick={onRetry}>
            {FAILURE_COPY.retry}
          </button>
        ) : (
          code !== "SAFETY_HOLD" && (
            <button type="button" className="cap-btn" onClick={() => navigate("/capture")}>
              {FAILURE_COPY.startOver}
            </button>
          )
        )}
        <button type="button" className="cap-link" onClick={onClose}>
          {FAILURE_COPY.close}
        </button>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------- Flow */

function CaptureFlow() {
  const { creationId } = useParams();
  const navigate = useNavigate();
  useFocusedCaptureHeader();

  const [creation, setCreation] = useState(null);
  const [vocabulary, setVocabulary] = useState(null);
  const [missing, setMissing] = useState(false);
  const [slow, setSlow] = useState(false);
  const [seenCreated, setSeenCreated] = useState(false);
  const pollStart = useRef(Date.now());

  const close = useCallback(() => navigate("/capture"), [navigate]);

  useEffect(() => {
    getVocabulary()
      .then(setVocabulary)
      .catch(() => setVocabulary(null));
  }, []);

  // Poll while the server is working; stop on any screen that waits for the
  // member or ends the flow.
  useEffect(() => {
    let cancelled = false;
    let handle = null;
    const tick = async () => {
      try {
        const next = await getCreation(creationId);
        if (cancelled) return;
        setCreation(next);
        setSlow(Date.now() - pollStart.current > SLOW_AFTER_MS);
        if (next && !WAITING.has(next.status)) handle = window.setTimeout(tick, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) setMissing(true);
        else handle = window.setTimeout(tick, POLL_MS * 2);
      }
    };
    if (!creation || !WAITING.has(creation.status)) tick();
    return () => {
      cancelled = true;
      if (handle) window.clearTimeout(handle);
    };
    // Re-arm only when the status changes (a member action moved it on).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creationId, creation?.status]);

  const update = (next) => {
    if (!next) return;
    pollStart.current = Date.now();
    setSlow(false);
    setCreation((prev) => ({ ...(prev || {}), ...next }));
  };

  const retry = async () => {
    try {
      const res = await generateDialogue(creationId);
      update(res?.creation || { status: CREATION_STATUS.SUMMARY_CONFIRMED, progress_step: 5 });
    } catch (e) {
      console.error("[Capture] retrying generation failed", e);
    }
  };

  let screen;
  if (missing) {
    screen = (
      <>
        <CaptureHeader title={FLOW_TITLE} onBack={close} />
        <div className="cap-body">
          <div className="cap-card cap-card--status">{errorText(null)}</div>
        </div>
      </>
    );
  } else if (!creation) {
    screen = <Analyzing creation={null} slow={false} onClose={close} />;
  } else if (creation.status === CREATION_STATUS.NEEDS_SOURCE_CONFIRMATION) {
    screen = (
      <ConfirmSource creation={creation} vocabulary={vocabulary} onUpdate={update} onClose={close} />
    );
  } else if (creation.status === CREATION_STATUS.SUMMARY_READY) {
    screen = <Summary creation={creation} onUpdate={update} onClose={close} />;
  } else if (creation.status === CREATION_STATUS.READY) {
    screen = seenCreated ? (
      <Ready creation={creation} onClose={close} />
    ) : (
      <Created onContinue={() => setSeenCreated(true)} onClose={close} />
    );
  } else if (
    creation.status === CREATION_STATUS.FAILED_RECOVERABLE ||
    creation.status === CREATION_STATUS.FAILED_TERMINAL
  ) {
    screen = <Failure creation={creation} onRetry={retry} onClose={close} />;
  } else {
    screen = <Analyzing creation={creation} slow={slow} onClose={close} />;
  }

  return (
    <CapturePage focused className="cap-flow">
      {screen}
    </CapturePage>
  );
}

export default CaptureFlow;
